// RationalPowerToRadicalTask.js
// a^(p/q) → √[q](a^p), z kanoniczną odpowiedzią: √[q]{ a^p (POLICZONE) }
// Akceptujemy też odpowiedzi równoważne numerycznie (np. √[3]{9^2} albo √[3]{3^4}).
// Gdy poprawne, lecz niekanoniczne — zwracamy obiekt z needsSimplification + expected + approx.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import RootTemplateInput from "../../../../components/inputs/RootTemplateInput";

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);

export function generateTask() {
  const base = pick([2, 3, 4, 5, 6, 7, 8, 9]); // bez 1
  const q = pick([2, 3, 4, 5]);

  // maksymalny licznik (jak masz w pliku); zostawiam tę samą logikę
  const pMax = base === 2 ? 6 : (base === 3 ? 4 : 3);

  // skracanie ułamka: wybieramy tylko p względnie pierwsze z q
  const gcd = (a, b) => {
    a = Math.abs(a); b = Math.abs(b);
    while (b) { const t = a % b; a = b; b = t; }
    return a || 1;
  };

  const candidates = range(1, pMax).filter((p) => gcd(p, q) === 1);
  const p = pick(candidates.length ? candidates : [1]); // fallback na 1, gdyby coś poszło nie tak

  return {
    type: "RationalPowerToRadicalTask",
    data: {
      base,
      p,
      q,
      leftLatex: `${base}^{\\tfrac{${p}}{${q}}}`, // zawsze skrócony ułamek
    },
  };
}



export function renderTask({ data }) {
  return (
    <div style={{ marginBottom: "12px" }}>
      Zapisz w postaci pierwiastka: <InlineMath math={data.leftLatex} />
    </div>
  );
}

/* ---------------- Parser odpowiedzi ----------------
   Rozpoznajemy:
   - sqrt(n), sqrt[q](n)
   - sqrt(a^p), sqrt[q](a^p)
   gdzie a,n,p,q to liczby całkowite (q>0).
*/
function parseRadical(raw) {
  if (!raw) return null;
  const s = String(raw).replace(/\s/g, "");

  // sqrt[q](a^p)
  let m = s.match(/^sqrt\[(\d+)\]\((\d+)\^\(?(-?\d+)\)?\)$/i);
  if (m) return { form: "pow", q: parseInt(m[1],10), a: parseInt(m[2],10), p: parseInt(m[3],10) };

  // sqrt(a^p)  => q domyślnie 2
  m = s.match(/^sqrt\((\d+)\^\(?(-?\d+)\)?\)$/i);
  if (m) return { form: "pow", q: 2, a: parseInt(m[1],10), p: parseInt(m[2],10) };

  // sqrt[q](n)
  m = s.match(/^sqrt\[(\d+)\]\((\d+)\)$/i);
  if (m) return { form: "num", q: parseInt(m[1],10), n: parseInt(m[2],10) };

  // sqrt(n) => q=2
  m = s.match(/^sqrt\((\d+)\)$/i);
  if (m) return { form: "num", q: 2, n: parseInt(m[1],10) };

  return null;
}

function sqrtLatex(q, inside) {
  return q === 2 ? `\\sqrt{${inside}}` : `\\sqrt[${q}]{${inside}}`;
}

/* ---------------- Input ---------------- */
export function renderInput(task, userAnswer, onChange, disabled) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <RootTemplateInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        placeholderInside="np. 49 lub 125"
        placeholderDegree="np. 3"
      />
    </div>
  );
}

/* ---------------- Sprawdzanie ----------------
   - Kanonicznie oczekujemy: √[q]{ base^p policzone }.
   - Akceptujemy też: √[q]{a^p} jeśli a^p = base^p, oraz √[q]{n} gdy n = base^p.
   - Jeśli odpowiedź dobra, ale nie kanoniczna => zwracamy obiekt z needsSimplification.
*/
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return false;
  const parsed = parseRadical(userAnswer.raw);
  if (!parsed) return false;

  const { base, p, q } = data;
  const expectedN = Math.pow(base, p);
  const canonicalLatex = sqrtLatex(q, expectedN);
  const approx = Math.pow(expectedN, 1 / q);
  const approxLatex = `\\approx ${approx.toFixed(3)}`;

  if (parsed.q !== q) return false;

  // numerycznie wprowadzona liczba
  if (parsed.form === "num") {
    if (parsed.n !== expectedN) return false;
    // to jest już kanoniczne (√[q]{n policzone})
    return true;
  }

  // postać potęgowa w środku — policz i porównaj
  if (parsed.form === "pow") {
    const val = Math.pow(parsed.a, parsed.p);
    if (val !== expectedN) return false;

    // poprawne, ale niekanoniczne (bo nienumer. w środku)
    return {
      correct: true,
      needsSimplification: true,
      expected: canonicalLatex,
      approx: approxLatex
    };
  }

  return false;
}

/* ---------------- Render user answer ---------------- */
export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw)
    return <i>(brak odpowiedzi)</i>;

  const parsed = parseRadical(userAnswer.raw);
  if (!parsed) return <InlineMath math={userAnswer.raw} />;

  if (parsed.form === "num") {
    return <InlineMath math={sqrtLatex(parsed.q, parsed.n)} />;
  }
  if (parsed.form === "pow") {
    return <InlineMath math={sqrtLatex(parsed.q, `${parsed.a}^{${parsed.p}}`)} />;
  }
  return <InlineMath math={userAnswer.raw} />;
}

/* ---------------- Kanoniczna odpowiedź i wyjaśnienie ---------------- */
export function renderAnswer({ data }) {
  const { base, p, q } = data;
  const n = Math.pow(base, p);
  return <InlineMath math={sqrtLatex(q, n)} />;
}

export function renderExplanation({ data }) {
  const { base, p, q, leftLatex } = data;
  const n = Math.pow(base, p);
  const def = "a^{\\tfrac{p}{q}}=\\sqrt[q]{\\,a^{p}\\,}\\quad (a>0,\\; q\\in\\mathbb{N}_{\\ge 2})";
  const step = `${leftLatex}\\;=\\;${sqrtLatex(q, `${base}^{${p}}`)}\\;=\\;${sqrtLatex(q, n)}`;
  return (
    <div style={{ display: "grid", rowGap: "8px" }}>
      <p><strong>Krok 1:</strong> Skorzystaj z definicji wykładnika wymiernego.</p>
      <BlockMath math={def} />
      <p><strong>Krok 2:</strong> Zapisz potęgę w postaci pierwiastka i policz potęgę pod znakiem:</p>
      <BlockMath math={step} />
    </div>
  );
}
